import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sunrise, Sun, Sunset, Moon, Clock, Lock, ArrowRight, PlayCircle } from 'lucide-react';
import api from '../lib/api';
import { MediaBadges, CategoryBadge } from './MediaBadges';

const BUCKETS = {
  dawn: { label: 'Dawn', icon: Sunrise, greeting: 'Begin before sunrise' },
  morning: { label: 'Morning', icon: Sunrise, greeting: 'Start your day gently' },
  afternoon: { label: 'Afternoon', icon: Sun, greeting: 'A midday reset' },
  evening: { label: 'Evening', icon: Sunset, greeting: 'Unwind into the evening' },
  night: { label: 'Night', icon: Moon, greeting: 'Settle before sleep' },
};

const currentBucket = () => {
  const h = new Date().getHours();
  if (h >= 4 && h < 8) return 'dawn';
  if (h >= 8 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 21) return 'evening';
  return 'night';
};

const PracticeByTime = () => {
  const [techs, setTechs] = useState([]);
  const bucket = currentBucket();
  const meta = BUCKETS[bucket];

  useEffect(() => {
    api.get('/meditations').then((r) => {
      const seen = new Set();
      const all = (r.data?.groups || []).flatMap((g) => g.techniques).filter((t) => {
        if (seen.has(t.id)) return false; seen.add(t.id); return true;
      });
      let match = all.filter((t) => t.time_of_day === bucket);
      if (match.length < 3) match = [...match, ...all.filter((t) => t.time_of_day === 'anytime' && !match.includes(t))];
      setTechs(match.slice(0, 4));
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  if (!techs.length) return null;
  const Icon = meta.icon;

  return (
    <section className="bg-cream-2 py-16 sm:py-20" data-testid="practice-by-time">
      <div className="max-w-6xl mx-auto px-5">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-rose-italic mb-2">
              <Icon className="w-4 h-4" /> For right now · {meta.label}
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-burgundy leading-tight">{meta.greeting}</h2>
            <p className="mt-2 text-mahogany/70 text-sm max-w-md">Meditations suited to this time of day — chosen to match where you are right now.</p>
          </div>
          <Link to="/meditations" className="pill-btn-outline shrink-0" data-testid="practice-by-time-all">Explore all <ArrowRight className="w-4 h-4" /></Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {techs.map((t) => (
            <Link key={t.id} to={`/meditations/${t.slug}`} data-testid={`ptime-card-${t.slug}`}
              className="group rounded-3xl overflow-hidden bg-white border border-rose/30 hover:border-rose/60 transition-colors shadow-sm hover:shadow-md flex flex-col">
              <div className="aspect-[16/11] bg-rose-soft/40 relative overflow-hidden">
                {t.hero_image
                  ? <img src={t.hero_image} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  : <div className="w-full h-full flex items-center justify-center text-mahogany/30"><PlayCircle className="w-10 h-10" /></div>}
                {t.locked && <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-mahogany/85 text-cream"><Lock className="w-3 h-3" /> {t.is_free ? 'Members' : (t.effective_price ? `$${t.effective_price}` : 'Premium')}</span>}
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-wider text-rose-italic mb-1">{t.purpose}</div>
                <h3 className="font-serif-display text-lg text-burgundy leading-snug">{t.title}</h3>
                <p className="mt-1 text-sm text-mahogany/70 line-clamp-2">{t.summary}</p>
                <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                  <CategoryBadge tech={t} />
                  <MediaBadges tech={t} className="ml-auto" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PracticeByTime;
